/**
 * Generates a thumbnail from a video blob.
 * Captures a frame at 0.5s.
 * @param videoBlob The video blob to generate a thumbnail from.
 * @returns A promise that resolves to the base64 encoded JPEG thumbnail (without data prefix).
 */
export const generateThumbnail = async (videoBlob: Blob): Promise<string> => {
    const objectUrl = URL.createObjectURL(videoBlob);
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.src = objectUrl;
        video.muted = true;
        video.playsInline = true;

        const onSeeked = () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(objectUrl);
                video.removeEventListener('seeked', onSeeked);
                return reject(new Error('Could not get canvas context'));
            }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
            URL.revokeObjectURL(objectUrl);
            video.removeEventListener('seeked', onSeeked);
            resolve(base64);
        };

        video.addEventListener('seeked', onSeeked);

        video.onloadeddata = () => {
            // Seek to 0.5s to avoid black frame at start
            video.currentTime = 0.5;
        };

        video.onerror = (e) => {
            URL.revokeObjectURL(objectUrl);
            reject(e);
        };

        video.load();
    });
};
